import { useState, useEffect, useCallback } from 'react';
import { type HeaderImageData, getStorageKey } from '../utils/headerImageStorage';

/**
 * Return type for the useArticleHeaderImage hook
 */
export interface UseArticleHeaderImageReturn {
  imageData: HeaderImageData | null;
  isLoading: boolean;
  error: string | null;
  saveImage: (data: HeaderImageData) => void;
  clearImage: () => void;
}

/**
 * Custom hook for managing article header images in session storage.
 * 
 * Handles loading, saving, and clearing header image data with proper
 * error handling for storage quota and security errors.
 * 
 * Requirements: 3.1, 3.2, 3.3, 3.4
 * 
 * @param articleId - The unique identifier of the article
 * @returns Object containing image data, loading state, error state, and storage functions
 */
export function useArticleHeaderImage(articleId: string): UseArticleHeaderImageReturn {
  const [imageData, setImageData] = useState<HeaderImageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load image data from session storage on mount
  useEffect(() => {
    setIsLoading(true);
    setError(null);

    try {
      const storageKey = getStorageKey(articleId);
      const storedData = sessionStorage.getItem(storageKey);

      if (storedData) {
        const parsed = JSON.parse(storedData) as HeaderImageData;
        setImageData(parsed);
      } else {
        setImageData(null);
      }
    } catch (err) {
      // Handle JSON parse errors - clear corrupted data
      console.error('Failed to load header image from storage:', err);
      try {
        const storageKey = getStorageKey(articleId);
        sessionStorage.removeItem(storageKey);
      } catch {
        // Ignore removal errors
      }
      setImageData(null);
    } finally {
      setIsLoading(false);
    }
  }, [articleId]);

  /**
   * Save image data to session storage
   * Handles QuotaExceededError and SecurityError gracefully
   */
  const saveImage = useCallback((data: HeaderImageData) => {
    setError(null);

    try {
      const storageKey = getStorageKey(articleId);
      const serialized = JSON.stringify(data);
      sessionStorage.setItem(storageKey, serialized);
      setImageData(data);
    } catch (err) {
      let errorMessage = 'Unable to save image to browser storage.';

      if (err instanceof DOMException) {
        if (err.name === 'QuotaExceededError') {
          errorMessage = 'Unable to save image to browser storage. Storage quota exceeded. You can still download the image.';
        } else if (err.name === 'SecurityError') {
          errorMessage = 'Unable to save image to browser storage. Storage access denied. You can still download the image.';
        }
      }

      console.error('Failed to save header image:', err);
      setError(errorMessage);
      
      // Still update local state so the image is displayed
      setImageData(data);
    }
  }, [articleId]);

  /**
   * Clear image data from session storage
   */
  const clearImage = useCallback(() => {
    setError(null);

    try {
      const storageKey = getStorageKey(articleId);
      sessionStorage.removeItem(storageKey);
    } catch (err) {
      console.error('Failed to clear header image from storage:', err);
      // Continue anyway - the important thing is to clear local state
    }

    setImageData(null);
  }, [articleId]);

  return {
    imageData,
    isLoading,
    error,
    saveImage,
    clearImage,
  };
}
